import {containsNonStandardAminoAcids} from 'biojs/Util';
import {
  getMaxAminoAcidCount,
  getMaxBulkPredictionItems,
  getMaxMultimerComponents,
  getMinAminoAcidCount,
  type ScientificModelNode,
} from 'model-hub/scientific-models/ScientificModelMetadata';

export type ComponentKind = 'aminoAcids' | 'smiles';

export type ComponentStatus = {kind: 'empty'} | {kind: 'ready'} | {kind: 'invalid'; reason: string};

export type InputNameError = 'blank' | 'duplicate' | 'tooLong';

export type InputStatus = 'empty' | 'incomplete' | 'ready' | 'invalid';

/**
 * A registry entity a component was picked from. The component's `value` holds the entity's fetched
 * residues or SMILES so length and charset checks read it like any typed value, but submit sends the
 * entity ID instead, so the prediction stays linked to the entity.
 */
export interface ComponentEntity {
  id: string;
  name: string;
  // Whether the user may run structure prediction on it. Entities they can't read never become
  // components at all.
  predictable: boolean;
}

export interface PredictionComponent {
  id: string;
  type: ComponentKind;
  value: string;
  entity?: ComponentEntity;
  // Number of identical copies of this component to submit. The backend config schema has no
  // stoichiometry field, so on submit each copy is materialized as a repeated component entry
  // (see buildPredictionConfig). Only meaningful for multimer models; pinned to 1 otherwise.
  copies: number;
  status: ComponentStatus;
}

/**
 * One input row in the New prediction inputs table. Each input owns its own component list and
 * becomes a single PredictionJob (one prediction config) on submit.
 */
export interface PredictionInput {
  id: string;
  // Human-readable label for this input, persisted to PredictionJob.name on submit.
  name: string;
  // Input-level name problem, recomputed by validateAllInputs. Unlike component statuses this is
  // set-wide: 'duplicate' depends on the other inputs in the batch.
  nameError: InputNameError | null;
  components: Array<PredictionComponent>;
}

export type PredictionInputsUpdate =
  | Array<PredictionInput>
  | ((currentInputs: Array<PredictionInput>) => Array<PredictionInput>);

/**
 * The most-restrictive combination of the selected models' capabilities. When multiple models are
 * selected, the inputs must satisfy every model, so booleans are AND-ed, "max" limits take the
 * minimum, and "min" limits take the maximum. MSA support is deliberately excluded — it does not
 * compose and is resolved per-model at submit (see getUseMSAForSubmission).
 */
export interface ComposedCapabilities {
  // False when no models are selected (editing is locked and there is no permissive set).
  supportsModels: boolean;
  supportsMultimers: boolean;
  supportsMolecules: boolean;
  maxComponents: number;
  minAminoAcidCount: number;
  maxAminoAcidCount: number;
  maxBulkPredictionItems: number;
}

const DEFAULT_MIN_AMINO_ACID_COUNT = 1;
const DEFAULT_MAX_AMINO_ACID_COUNT = 1500;

export interface SubmitDisabledReasonParams {
  inputs: Array<PredictionInput>;
  hasModels: boolean;
  hasSelectedModel: boolean;
  isSubmitting: boolean;
  maxAminoAcidCount: number;
  // Max components allowed per prediction for the selected model. Copies expand into repeated
  // components on submit, so the summed copies per input must not exceed this.
  maxComponentsPerInput: number;
  maxInputs: number;
  minAminoAcidCount: number;
  modelName: string;
  moleculesSupported: boolean;
  multimersSupported: boolean;
  // Hosts that pick the output folder client-side require one before submit; others resolve it
  // server-side.
  folderRequired?: boolean;
  hasFolder?: boolean;
}

const AMINO_ACID_REGEX = /^[ACDEFGHIKLMNPQRSTVWY]+$/;
// This is intentionally only a permissive plausibility check. The backend remains authoritative
// for SMILES grammar, valence, balanced delimiters, and CCD existence.
const SMILES_CHARSET_REGEX = /^[A-Za-z0-9@+\-[\]()=#:/\\.$%*_]+$/;
const OBVIOUS_NON_SMILES_VALUES = new Set(['N/A']);

// Mirrors PREDICTION_JOB_NAME_MAX_LENGTH in
// benchling/model_hub/persistence/prediction_job_table.py.
export const MAX_INPUT_NAME_LENGTH = 263;

let nextComponentId = 0;
let nextInputId = 0;

export function makeComponent(type: ComponentKind = 'aminoAcids'): PredictionComponent {
  nextComponentId += 1;
  return {
    id: `new-prediction-component-${nextComponentId}`,
    type,
    value: '',
    copies: 1,
    status: {kind: 'empty'},
  };
}

export function makeInput(name = '', components?: Array<PredictionComponent>): PredictionInput {
  nextInputId += 1;
  return {
    id: `new-prediction-input-${nextInputId}`,
    name,
    nameError: null,
    components: components ?? [makeComponent('aminoAcids')],
  };
}

export function makeEntityComponent(
  type: ComponentKind,
  value: string,
  entity: ComponentEntity
): PredictionComponent {
  return validateComponent({...makeComponent(type), entity, value});
}

/**
 * One input per entity component, named after its entity. Registry names needn't be unique, so a name
 * already in the batch gets a numbered suffix rather than a duplicate-name error on arrival.
 */
export function makeEntityInputs(
  components: Array<PredictionComponent>,
  initiallyUsedNames: ReadonlySet<string>
): Array<PredictionInput> {
  const usedNames = new Set(initiallyUsedNames);
  return components.map((component) => {
    const baseName = component.entity?.name.trim() ?? '';
    let name = baseName;
    for (let suffix = 2; name.length === 0 || usedNames.has(name); suffix++) {
      name = baseName.length === 0 ? makeDefaultInputName(usedNames) : `${baseName} (${suffix})`;
    }
    usedNames.add(name);
    return makeInput(name, [component]);
  });
}

/** First unused `input-N`, scanning from 1. Skips names already taken (explicit or generated). */
export function makeDefaultInputName(usedNames: ReadonlySet<string>): string {
  let index = 1;
  while (usedNames.has(`input-${index}`)) {
    index += 1;
  }
  return `input-${index}`;
}

/** Assign a default name to every input that has none. Call only at creation/import time. */
export function fillMissingInputNames(
  inputs: Array<PredictionInput>,
  initiallyUsedNames: ReadonlySet<string> = new Set()
): Array<PredictionInput> {
  const usedNames = new Set(initiallyUsedNames);
  for (const input of inputs) {
    const name = input.name.trim();
    if (name.length > 0) {
      usedNames.add(name);
    }
  }
  return inputs.map((input) => {
    if (input.name.trim().length > 0) {
      return input;
    }
    const name = makeDefaultInputName(usedNames);
    usedNames.add(name);
    return {...input, name};
  });
}

/** Row label for display/aria. Blank names get a positional stand-in so controls stay labelled. */
export function getInputLabel(input: PredictionInput, index: number): string {
  return input.name.trim() || `Unnamed input ${index + 1}`;
}

export function getComponentLabel(components: Array<PredictionComponent>, index: number): string {
  let aminoAcidCount = 0;
  let ligandCount = 0;
  for (let i = 0; i <= index; i++) {
    if (components[i].type === 'aminoAcids') {
      aminoAcidCount += 1;
    } else {
      ligandCount += 1;
    }
  }

  if (components[index].type === 'aminoAcids') {
    return `Chain ${getComponentLetter(aminoAcidCount)}`;
  }
  return `Ligand ${getComponentLetter(ligandCount)}`;
}

export function getSubmitDisabledReason({
  inputs,
  hasModels,
  hasSelectedModel,
  isSubmitting,
  maxAminoAcidCount,
  maxComponentsPerInput,
  maxInputs,
  minAminoAcidCount,
  modelName,
  moleculesSupported,
  multimersSupported,
  folderRequired = false,
  hasFolder = false,
}: SubmitDisabledReasonParams): string | null {
  if (isSubmitting) {
    return 'Submitting...';
  }
  if (!hasModels) {
    return 'No models available';
  }
  if (!hasSelectedModel) {
    return 'Select a model';
  }
  if (inputs.length === 0) {
    return 'Add at least one input';
  }
  if (inputs.length > maxInputs) {
    return `${modelName} supports at most ${maxInputs} ${maxInputs === 1 ? 'input' : 'inputs'} per batch`;
  }
  const nameErrors = getInputNameErrors(inputs);
  if (nameErrors.some((error) => error === 'blank')) {
    return 'Name every input';
  }
  if (nameErrors.some((error) => error === 'duplicate')) {
    return 'Input names must be unique';
  }
  if (nameErrors.some((error) => error === 'tooLong')) {
    return `Input names must be at most ${MAX_INPUT_NAME_LENGTH} characters`;
  }
  // Copies expand into repeated components on submit; the summed copies for any input must fit the
  // model's component cap (the backend rejects lists longer than this).
  if (inputs.some((input) => getTotalCopies(input.components) > maxComponentsPerInput)) {
    return `Reduce copies: at most ${maxComponentsPerInput} ${
      multimersSupported ? 'components' : 'component'
    } per prediction`;
  }
  const allComponents = inputs.flatMap((input) => input.components);
  if (allComponents.some((component) => normalizeValue(component).length === 0)) {
    return multimersSupported ? 'Enter a value for every component' : 'Enter a value for the component';
  }
  // Validate freshly rather than trusting the stored status, so a non-empty value with a stale
  // "empty" status (e.g. edited but not yet re-validated) can't slip through as submittable.
  if (allComponents.some((component) => validateComponent(component).status.kind === 'invalid')) {
    return multimersSupported ? 'Fix invalid components' : 'Fix the invalid component';
  }
  if (allComponents.some((component) => component.type === 'smiles') && !moleculesSupported) {
    return `${modelName} does not support ligands`;
  }
  if (
    inputs.some((input) => {
      const aminoAcidCount = getTotalAminoAcidCount(input.components);
      return aminoAcidCount < minAminoAcidCount || aminoAcidCount > maxAminoAcidCount;
    })
  ) {
    return `Protein sequences must be between ${minAminoAcidCount} and ${maxAminoAcidCount} amino acids`;
  }
  if (folderRequired && !hasFolder) {
    return 'Select an output folder';
  }
  return null;
}

/** Total residues submitted for an input, including repeated copies of amino acid components. */
function getTotalAminoAcidCount(components: Array<PredictionComponent>): number {
  return components.reduce(
    (total, component) =>
      component.type === 'aminoAcids'
        ? total + normalizeValue(component).length * Math.max(1, Math.floor(component.copies))
        : total,
    0
  );
}

/** Sum of copies across an input's components (each copy materializes as one component on submit). */
export function getTotalCopies(components: Array<PredictionComponent>): number {
  return components.reduce((total, component) => total + Math.max(1, Math.floor(component.copies)), 0);
}

/**
 * Aggregate the per-component statuses into a single input-level status. Intended to be read
 * after validateAll has run, so component statuses reflect the latest values.
 */
export function getInputStatus(input: Pick<PredictionInput, 'components' | 'nameError'>): InputStatus {
  if (
    input.components.some((component) => component.status.kind === 'invalid') ||
    input.nameError === 'duplicate' ||
    input.nameError === 'tooLong'
  ) {
    return 'invalid';
  }
  if (input.nameError === 'blank') {
    return 'incomplete';
  }
  if (input.components.every((component) => component.status.kind === 'ready')) {
    return 'ready';
  }
  if (input.components.every((component) => component.status.kind === 'empty')) {
    return 'empty';
  }
  return 'incomplete';
}

/**
 * Compose the most-restrictive capability set across the selected models. Every input must satisfy
 * every selected model, so booleans are AND-ed, "max" limits take the minimum, and "min" limits take
 * the maximum. MSA is intentionally excluded (resolved per-model at submit).
 */
export function composeModelCapabilities(models: ReadonlyArray<ScientificModelNode>): ComposedCapabilities {
  if (models.length === 0) {
    return {
      supportsModels: false,
      supportsMultimers: false,
      supportsMolecules: false,
      maxComponents: 1,
      minAminoAcidCount: DEFAULT_MIN_AMINO_ACID_COUNT,
      maxAminoAcidCount: DEFAULT_MAX_AMINO_ACID_COUNT,
      // No model selected means no inputs can run yet; keep the batch cap at 0 so the editor stays locked.
      maxBulkPredictionItems: 0,
    };
  }
  return {
    supportsModels: true,
    supportsMultimers: models.every((model) => model.capabilities.supportsMultimers),
    supportsMolecules: models.every((model) => model.capabilities.supportsMultimerMolecules),
    maxComponents: Math.min(...models.map((model) => getMaxMultimerComponents(model))),
    minAminoAcidCount: Math.max(...models.map((model) => getMinAminoAcidCount(model))),
    maxAminoAcidCount: Math.min(...models.map((model) => getMaxAminoAcidCount(model))),
    maxBulkPredictionItems: Math.min(...models.map((model) => getMaxBulkPredictionItems(model))),
  };
}

/**
 * Input-level status under a composed capability set. Structural mismatches (too many components for
 * the composed cap, or a ligand when molecules are unsupported) are always invalid — the data is kept
 * (flag, never destroy), only the derived status changes. Magnitude limits (composed AA min/max) gate
 * only an otherwise-ready input, so an empty/incomplete input isn't spuriously flagged.
 *
 * Deliberately name-blind: name problems (blank/duplicate/too long) are surfaced on the name field
 * itself, not on the row's status badge, so `nameError: null` is passed here on purpose.
 */
export function getInputStatusForCapabilities(
  components: Array<PredictionComponent>,
  capabilities: ComposedCapabilities
): InputStatus {
  return classifyInput(components, capabilities).status;
}

/**
 * Every determinable reason an input has its status, for the row's status-badge tooltip. Empty for a
 * ready input (no tooltip). Shares the exact branch order with getInputStatusForCapabilities (both
 * delegate to classifyInput), so the reasons always match the badge. Component-level reasons name the
 * offending component (e.g. "Chain B …") using getComponentLabel.
 */
export function getInputStatusReasons(
  components: Array<PredictionComponent>,
  capabilities: ComposedCapabilities
): Array<string> {
  return classifyInput(components, capabilities).reasons;
}

/**
 * Single source of truth for an input's composed status and its explanations. Collects *every*
 * determinable failure so the tooltip can surface them all at once, in the order a user should fix
 * them: structural checks first (component count, then ligand support), then per-component validity
 * (SMILES / amino-acid charset, one reason per invalid component), then AA magnitude. The magnitude
 * check runs only when every component is filled and valid (baseStatus === 'ready'), since a length
 * number is meaningless while a component is still empty or malformed. The status is `invalid` when
 * any of these failures are present; otherwise it is the fill state (incomplete / empty / ready).
 */
function classifyInput(
  components: Array<PredictionComponent>,
  capabilities: ComposedCapabilities
): {status: InputStatus; reasons: Array<string>} {
  const reasons: Array<string> = [];
  // Copies expand into repeated components on submit, so the cap applies to the summed copies — the
  // same quantity getSubmitDisabledReason checks. Counting raw components here would let a row read
  // Ready while submit is blocked on "Reduce copies".
  if (getTotalCopies(components) > capabilities.maxComponents) {
    reasons.push(
      capabilities.maxComponents === 1
        ? 'Selected models only support 1 component per input'
        : `Selected models support at most ${capabilities.maxComponents} components per input`
    );
  }
  if (!capabilities.supportsMolecules && components.some((component) => component.type === 'smiles')) {
    reasons.push("Ligands aren't supported by the selected models");
  }
  const validated = validateAll(components);
  validated.forEach((component, index) => {
    if (component.status.kind !== 'invalid') {
      return;
    }
    const label = getComponentLabel(components, index);
    if (component.entity != null && !component.entity.predictable) {
      reasons.push(`You don't have permission to run predictions on ${component.entity.name}`);
      return;
    }
    reasons.push(
      component.type === 'smiles'
        ? `${label} has an invalid SMILES string`
        : `${label} contains non-standard amino acid letters`
    );
  });
  // Name-blind by design: name problems (blank/duplicate/too long) are surfaced on the name field
  // rather than in this badge/tooltip, so `nameError: null` is passed on purpose.
  const baseStatus = getInputStatus({components: validated, nameError: null});
  // Magnitude only makes sense once every component is filled and valid; an empty/incomplete/invalid
  // input has no meaningful total length to compare against the composed min/max.
  if (baseStatus === 'ready') {
    const aminoAcidCount = getTotalAminoAcidCount(components);
    if (aminoAcidCount < capabilities.minAminoAcidCount) {
      reasons.push(
        `Selected models require at least ${capabilities.minAminoAcidCount} amino acids per input`
      );
    }
    if (aminoAcidCount > capabilities.maxAminoAcidCount) {
      reasons.push(`Selected models support at most ${capabilities.maxAminoAcidCount} amino acids per input`);
    }
  }
  if (reasons.length > 0) {
    return {status: 'invalid', reasons};
  }
  if (baseStatus === 'incomplete') {
    return {status: 'incomplete', reasons: ['Enter a value for every component']};
  }
  if (baseStatus === 'empty') {
    return {status: 'empty', reasons: ['Enter a value to run this input']};
  }
  return {status: 'ready', reasons: []};
}

/**
 * Structural-only compatibility of a single model against the current inputs, for disabling it in the
 * picker. Returns an explanatory reason when the inputs' *shape* can't be run by the model (a
 * multi-component input on a monomer-only model, or a ligand on a molecule-unsupported model), or null
 * when compatible. Magnitude problems (sequence too long, too many inputs) never disable a model —
 * they surface as per-row invalid flags / submit-gate reasons instead.
 */
export function getModelStructuralIncompatibility(
  model: ScientificModelNode,
  inputs: Array<PredictionInput>
): string | null {
  const inputsContainMultimers = inputs.some((input) => input.components.length > 1);
  if (inputsContainMultimers && !model.capabilities.supportsMultimers) {
    return "Input contains multiple components, which this model doesn't support";
  }
  const inputsContainLigand = inputs.some((input) =>
    input.components.some((component) => component.type === 'smiles')
  );
  if (inputsContainLigand && !model.capabilities.supportsMultimerMolecules) {
    return "Input contains a ligand, which this model doesn't support";
  }
  return null;
}

export function normalizeValue(component: PredictionComponent): string {
  if (component.type === 'aminoAcids') {
    return component.value.toUpperCase().replace(/\s/g, '');
  }
  return component.value.trim();
}

export function validateAllInputs(inputs: Array<PredictionInput>): Array<PredictionInput> {
  const nameErrors = getInputNameErrors(inputs);
  return inputs.map((input, index) => ({
    ...input,
    components: validateAll(input.components),
    nameError: nameErrors[index],
  }));
}

/**
 * Per-input name errors, positionally aligned with `inputs`. Duplicates are compared on the
 * trimmed name, case-sensitively, matching the backend.
 */
export function getInputNameErrors(inputs: Array<PredictionInput>): Array<InputNameError | null> {
  const counts = new Map<string, number>();
  for (const input of inputs) {
    const name = input.name.trim();
    if (name.length > 0) {
      counts.set(name, (counts.get(name) ?? 0) + 1);
    }
  }
  return inputs.map((input) => {
    const name = input.name.trim();
    if (name.length === 0) {
      return 'blank';
    }
    if (name.length > MAX_INPUT_NAME_LENGTH) {
      return 'tooLong';
    }
    if ((counts.get(name) ?? 0) > 1) {
      return 'duplicate';
    }
    return null;
  });
}

export function validateAll(components: Array<PredictionComponent>): Array<PredictionComponent> {
  return components.map((component) => validateComponent(component));
}

export function validateComponent(component: PredictionComponent): PredictionComponent {
  const value = normalizeValue(component);
  if (value.length === 0) {
    return {...component, value, status: {kind: 'empty'}};
  }
  if (component.entity != null && !component.entity.predictable) {
    return {
      ...component,
      value,
      status: {kind: 'invalid', reason: "You don't have permission to run predictions on this entity"},
    };
  }
  if (component.type === 'smiles') {
    if (!SMILES_CHARSET_REGEX.test(value) || OBVIOUS_NON_SMILES_VALUES.has(value.toUpperCase())) {
      return {
        ...component,
        value,
        status: {
          kind: 'invalid',
          reason: "Contains characters that aren't valid in a SMILES string",
        },
      };
    }
    return {...component, value, status: {kind: 'ready'}};
  }
  if (!AMINO_ACID_REGEX.test(value) || containsNonStandardAminoAcids(value)) {
    return {
      ...component,
      value,
      status: {kind: 'invalid', reason: 'Use standard amino acid letters only'},
    };
  }
  return {...component, value, status: {kind: 'ready'}};
}

export function getComponentLetter(count: number): string {
  if (count <= 26) {
    return String.fromCharCode(64 + count);
  }
  return String(count);
}
